import axios from 'axios';
import { format } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ClickableMetric from '../components/ClickableMetric';
import {
  getFocusModeTailwindClasses,
  getSelectableFocusModes,
} from '../constants/focusModes';
import { useOrganization } from '../context/OrganizationContext';
import { useOrganizationAccess } from '../hooks/useOrganizationAccess';
import { formatCurrency } from '../utils/currency';
import { formatFocusMode } from '../utils/enumFormatting';
import {
  logSAMChatResponse,
  validateSAMChatResponse,
} from '../utils/samChatHelpers';
import { TaskKanbanView, TaskListView } from '../components/GravityPlan';
import ResearchTab from '../components/ResearchTab';
import { ResearchEntityType } from '../services/researchService';
import { masterPlanTaskService, MasterPlanTask } from '../services/masterPlan';
import { okrService, OrganizationOKR } from '../services/okrs';
import { getAuthHeaders } from '../utils/auth';

import { API_URL } from '../constants/urls';

// Track which positioning records we've already tried to save to avoid API spam
const savedPositioningRecords = new Set<string>();

// Helper function to calculate positioning score when it's null in the database
function calculatePositioningScore(
  position: any,
  organizationId?: string,
): number {
  // If we have a stored score, use it
  if (position?.obvious_choice_score != null) {
    return position.obvious_choice_score;
  }

  // Otherwise calculate from components (treat null as 0)
  const propScore = position?.proposition_score_snapshot ?? 0;
  const segScore = position?.segment_score_snapshot ?? 0;
  const relevanceMultiplier = position?.relevance_multiplier ?? 1.0;

  const baseScore = (propScore + segScore) / 2;
  const calculatedScore = baseScore * relevanceMultiplier;

  // Only save if we haven't already tried to save this positioning record
  const recordKey = `${position?.proposition_id}-${position?.segment_id}`;
  if (
    organizationId &&
    position?.proposition_id &&
    position?.segment_id &&
    !savedPositioningRecords.has(recordKey)
  ) {
    savedPositioningRecords.add(recordKey);

    // Make async API call to save the positioning score (fire and forget)
    setTimeout(() => {
      const positionId = position.id || position.positioning_id;
      if (!positionId) {
        // If no position ID, skip the update
        savedPositioningRecords.delete(recordKey);
        return;
      }
      axios
        .put(
          `${API_URL}/positions/${positionId}?organization_id=${organizationId}`,
          {
            obvious_choice_score: calculatedScore,
          },
          {
            headers: getAuthHeaders(),
          },
        )
        .catch((_err) => {
          // console.warn removed
          // Remove from set on failure so it can be retried later
          savedPositioningRecords.delete(recordKey);
        });
    }, Math.random() * 1000); // Random delay up to 1 second
  }

  return calculatedScore;
}

interface PositionData {
  id: string;
  segment_id: string;
  proposition_id: string;
  focus_mode: string | null;
  owner: string | null;
  owner_id: string | null;
  why_obvious_choice: string | null;
  gaps_to_address: string | null;
  actions_to_improve: string | null;
  rationale: string | null;
  competitive_alternatives: string[] | null;
  unique_attributes: string[] | null;
  differentiated_benefits: string[] | null;
  relevant_trends: string[] | null;
  sam_value: number | null;
  som_value: number | null;
  sam_evidence: string | null;
  sam_calculation_notes: string | null;
  tam_value: number | null;
  pricing_model: string | null;
  pricing_model_id: string | null;
  units_per_customer?: number | null;
  som_timeframe_years?: number | null;
  obvious_choice_score: number | null;
  relevance_multiplier: number | null;
  proposition_score_snapshot: number | null;
  segment_score_snapshot: number | null;
  strategic_importance: number | null;
  strategic_importance_evidence: string | null;
  proposition_fit: number | null;
  proposition_fit_evidence: string | null;
  competitive_landscape: number | null;
  competitive_landscape_evidence: string | null;
  market_timing: number | null;
  market_timing_evidence: string | null;
  obvious_choice_score_updated_at: string | null;
  relevance_multiplier_updated_at: string | null;
  estimated_num_of_sam_companies?: number | null;
  estimated_num_of_som_companies?: number | null;
  // Business metrics from analytics endpoint
  opportunity_count?: number;
  won_opportunities_count?: number;
  open_opportunities_count?: number;
  companies_count?: number;
  case_studies_count?: number;
  testimonials_count?: number;
  awards_count?: number;
  booked_business?: number;
  lost_business?: number;
  open_pipeline_unweighted?: number;
  open_pipeline_weighted?: number;
  // Names from analytics endpoint
  proposition_name?: string;
  segment_name?: string;
}

interface BusinessMetrics {
  total_pipeline_value: number;
  total_closed_won_value: number;
  total_opportunities: number;
  active_opportunities: number;
  segment_name: string;
  proposition_name: string;
  org_currency: string;
  companies_count: number;
  case_studies_count: number;
  testimonials_count: number;
  awards_count: number;
  segment_company_count?: number;
  segment_tam?: number;
  estimated_num_of_sam_companies?: number;
  estimated_num_of_som_companies?: number;
}

interface SAMChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface PricingModel {
  id: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  pricing_type?: string;
  frequency?: string;
  annual_price?: number;
}

const focusModeOptions = getSelectableFocusModes();

const _getScoreChipColor = (score: number) => {
  if (score >= 7) return 'bg-green-100 text-green-800 border-green-300';
  if (score >= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  return 'bg-red-100 text-red-800 border-red-300';
};

const formatPricingType = (type: string | null | undefined): string => {
  if (!type) return '';
  // Convert snake_case to Title Case
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const relevanceComponents = [
  {
    key: 'strategic_importance',
    label: 'Strategic Importance',
    description: 'Alignment with company strategy and growth plans',
    min: 0.8,
    max: 1.3,
    step: 0.05,
  },
  {
    key: 'proposition_fit',
    label: 'Proposition Fit',
    description: 'How well the proposition meets segment needs',
    min: 0.8,
    max: 1.3,
    step: 0.05,
  },
  {
    key: 'competitive_landscape',
    label: 'Competitive Landscape',
    description: 'Market opportunity and competitive advantage',
    min: 0.8,
    max: 1.3,
    step: 0.05,
  },
  {
    key: 'market_timing',
    label: 'Market Timing',
    description: 'Market readiness and timing factors',
    min: 0.8,
    max: 1.3,
    step: 0.05,
  },
];

export default function PositionDetailsTailwind() {
  const { positionId } = useParams<{
    positionId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrg, canEdit, isAdmin } = useOrganization();
  const { hasGenAIAccess, planType } = useOrganizationAccess();

  // Function to get initial tab from URL hash
  const getInitialTab = (): 'overview' | 'tasks' | 'research' => {
    const hash = location.hash.replace('#', '');
    if (hash === 'tasks' || hash === 'research') {
      return hash;
    }
    return 'overview';
  };

  const [position, setPosition] = useState<PositionData | null>(null);
  // Derived from position data - needed by child components
  const [segmentId, setSegmentId] = useState<string | undefined>(undefined);
  const [propositionId, setPropositionId] = useState<string | undefined>(
    undefined,
  );
  const [businessMetrics, setBusinessMetrics] =
    useState<BusinessMetrics | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [frameworkEditMode, setFrameworkEditMode] = useState(false);
  const [marketEditMode, setMarketEditMode] = useState(false);
  const [editedPosition, setEditedPosition] = useState<any>({});
  const [editedFramework, setEditedFramework] = useState<any>({
    competitive_alternatives: '',
    unique_attributes: '',
    differentiated_benefits: '',
    relevant_trends: '',
  });
  const [editedMarket, setEditedMarket] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pricingModels, setPricingModels] = useState<PricingModel[]>([]);
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);

  // SAM Calculator state
  const [showSAMCalculator, setShowSAMCalculator] = useState(false);
  const [samChatMessages, setSamChatMessages] = useState<SAMChatMessage[]>([]);
  const [samChatInput, setSamChatInput] = useState('');
  const [samChatLoading, setSamChatLoading] = useState(false);
  const [suggestedEstimate, setSuggestedEstimate] = useState<{
    company_count: number;
    reasoning: string;
    confidence?: string;
  } | null>(null);
  const [showSamSuccess, setShowSamSuccess] = useState(false);

  // SAM Calculator input ref for auto-focus
  const samChatInputRef = useRef<HTMLInputElement>(null);

  // Market Relevance scoring state
  const [scoringEditMode, setScoringEditMode] = useState(false);
  const [editedScoring, setEditedScoring] = useState<any>({});

  // State for tabs
  const [activeTab, setActiveTab] = useState<'overview' | 'tasks' | 'research'>(
    getInitialTab(),
  );
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [taskViewMode, setTaskViewMode] = useState<'kanban' | 'list'>('kanban');
  const [allOKRs, setAllOKRs] = useState<OrganizationOKR[]>([]);

  // Function to handle tab changes and update URL
  const handleTabChange = (tab: 'overview' | 'tasks' | 'research') => {
    setActiveTab(tab);
    window.history.replaceState(null, '', `#${tab}`);
  };
  const [organizationMembersForTasks, setOrganizationMembersForTasks] =
    useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);

  // Track previous organization to detect changes
  const prevOrgRef = React.useRef<string | null>(null);

  // Listen for hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'tasks' || hash === 'research') {
        setActiveTab(hash);
      } else {
        setActiveTab('overview');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    // If organization changed, navigate to positioning page
    if (
      prevOrgRef.current &&
      selectedOrg?.id &&
      prevOrgRef.current !== selectedOrg.id
    ) {
      // Organization changed logging removed
      navigate('/positioning');
      return;
    }

    // Update the ref for next render
    if (selectedOrg?.id) {
      prevOrgRef.current = selectedOrg.id;
    }

    if (selectedOrg && positionId) {
      setLoading(true);

      // Fetch complete position data in a single optimized request
      axios
        .get(
          `${API_URL}/positions/${positionId}/complete?organization_id=${selectedOrg.id}`,
          { headers: getAuthHeaders() },
        )
        .then((res) => {
          const data = res.data;

          // Set position data
          const normalizedData = {
            ...data.position,
            strategic_importance: data.position.strategic_importance ?? 1.0,
            proposition_fit: data.position.proposition_fit ?? 1.0,
            competitive_landscape: data.position.competitive_landscape ?? 1.0,
            market_timing: data.position.market_timing ?? 1.0,
          };

          setPosition(normalizedData);
          setEditedPosition(normalizedData);

          // Extract segment and proposition IDs for child components
          setSegmentId(normalizedData.segment_id);
          setPropositionId(normalizedData.proposition_id);

          // Set all related data from complete response
          setPricingModels(data.pricing_models);
          setOrganizationMembers(data.organization_members);
          setOrganizationMembersForTasks(data.organization_members);
          setAllOKRs(data.okrs);
          setAreas(data.areas);
          setTasks(data.tasks);

          // Initialize edit forms
          setEditedFramework({
            ...normalizedData,
            competitive_alternatives: Array.isArray(
              normalizedData.competitive_alternatives,
            )
              ? normalizedData.competitive_alternatives.join('\n')
              : normalizedData.competitive_alternatives || '',
            unique_attributes: Array.isArray(normalizedData.unique_attributes)
              ? normalizedData.unique_attributes.join('\n')
              : normalizedData.unique_attributes || '',
            differentiated_benefits: Array.isArray(
              normalizedData.differentiated_benefits,
            )
              ? normalizedData.differentiated_benefits.join('\n')
              : normalizedData.differentiated_benefits || '',
            relevant_trends: Array.isArray(normalizedData.relevant_trends)
              ? normalizedData.relevant_trends.join('\n')
              : normalizedData.relevant_trends || '',
          });

          setEditedScoring(normalizedData);
          setEditedMarket({
            pricing_model: normalizedData.pricing_model,
            pricing_model_id: normalizedData.pricing_model_id,
            sam_evidence:
              normalizedData.sam_evidence ||
              normalizedData.sam_calculation_notes,
            sam_value: normalizedData.sam_value,
            som_value: normalizedData.som_value,
            estimated_num_of_sam_companies:
              normalizedData.estimated_num_of_sam_companies,
            estimated_num_of_som_companies:
              normalizedData.estimated_num_of_som_companies,
            units_per_customer: normalizedData.units_per_customer || 1,
            som_timeframe_years: normalizedData.som_timeframe_years || 3,
          });

          // Update business metrics
          updateBusinessMetricsFromPosition(normalizedData);

          setLoading(false);
        })
        .catch((_err) => {
          setError('Failed to load position data');
          setLoading(false);
        });
    }
  }, [selectedOrg, positionId, navigate]);

  // Task-related functions (fetchTasks only used for refresh after updates)
  const fetchTasks = async (positionId?: string) => {
    if (!selectedOrg) return;

    // Use provided positionId or fall back to position state
    const idToUse = positionId || position?.id;
    if (!idToUse) return;

    try {
      console.log('Fetching tasks for position:', idToUse);
      const fetchedTasks = await masterPlanTaskService.list(selectedOrg.id, {
        position_ids: [idToUse], // Use the actual position ID
        include_org_tasks: false, // Only show position-specific tasks
      });
      console.log('Fetched tasks:', fetchedTasks);
      setTasks(fetchedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchOKRs = async () => {
    if (!selectedOrg) return;

    try {
      const okrs = await okrService.listOKRs(selectedOrg.id);
      // Filter for only draft and active OKRs
      const activeOKRs = okrs.filter(
        (okr) => okr.status === 'draft' || okr.status === 'active',
      );
      setAllOKRs(activeOKRs);
    } catch (error) {
      console.error('Error fetching OKRs:', error);
      setAllOKRs([]);
    }
  };

  const handleTaskUpdate = async (
    taskId: string,
    updates: Partial<MasterPlanTask>,
  ) => {
    if (!selectedOrg) return;
    try {
      await masterPlanTaskService.update(selectedOrg.id, taskId, updates);
      await fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleTaskCreate = async (task: MasterPlanTask) => {
    // TaskModal already creates the task, just refresh the list
    if (!selectedOrg) return;
    await fetchTasks();
  };

  const handleTaskDelete = async (taskId: string) => {
    if (!selectedOrg) return;
    try {
      await masterPlanTaskService.delete(selectedOrg.id, taskId);
      await fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const fetchPositionData = async () => {
    try {
      setLoading(true);

      // Fetch the full position details using the UUID from the URL
      const detailResponse = await axios.get(
        `${API_URL}/positions/${positionId}?organization_id=${selectedOrg?.id}`,
        { headers: getAuthHeaders() },
      );

      const fullPositionData = detailResponse.data;

      // Initialize market relevance values to 1.0 if null
      const normalizedData = {
        ...fullPositionData,
        id: fullPositionData.id || positionId, // Ensure we have an id
        strategic_importance: fullPositionData.strategic_importance ?? 1.0,
        proposition_fit: fullPositionData.proposition_fit ?? 1.0,
        competitive_landscape: fullPositionData.competitive_landscape ?? 1.0,
        market_timing: fullPositionData.market_timing ?? 1.0,
      };

      setPosition(normalizedData);
      setEditedPosition(normalizedData);

      // Fetch tasks for this position now that we have the position ID
      if (normalizedData.id) {
        fetchTasks(normalizedData.id);
      }
      // Convert arrays to newline-separated strings for editing
      setEditedFramework({
        ...normalizedData,
        competitive_alternatives: Array.isArray(
          normalizedData.competitive_alternatives,
        )
          ? normalizedData.competitive_alternatives.join('\n')
          : normalizedData.competitive_alternatives || '',
        unique_attributes: Array.isArray(normalizedData.unique_attributes)
          ? normalizedData.unique_attributes.join('\n')
          : normalizedData.unique_attributes || '',
        differentiated_benefits: Array.isArray(
          normalizedData.differentiated_benefits,
        )
          ? normalizedData.differentiated_benefits.join('\n')
          : normalizedData.differentiated_benefits || '',
        relevant_trends: Array.isArray(normalizedData.relevant_trends)
          ? normalizedData.relevant_trends.join('\n')
          : normalizedData.relevant_trends || '',
      });
      setEditedScoring(normalizedData);
      setEditedMarket({
        pricing_model: normalizedData.pricing_model,
        pricing_model_id: normalizedData.pricing_model_id,
        sam_evidence:
          normalizedData.sam_evidence || normalizedData.sam_calculation_notes,
        sam_value: normalizedData.sam_value,
        som_value: normalizedData.som_value,
        estimated_num_of_sam_companies:
          normalizedData.estimated_num_of_sam_companies,
        estimated_num_of_som_companies:
          normalizedData.estimated_num_of_som_companies,
        units_per_customer: normalizedData.units_per_customer || 1,
        som_timeframe_years: normalizedData.som_timeframe_years || 3,
      });

      // Now that we have position data, update business metrics
      await updateBusinessMetricsFromPosition(normalizedData);
    } catch (err) {
      // console.error removed
      setError('Failed to load position data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingModels = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/propositions/${propositionId}/pricing?organization_id=${selectedOrg?.id}`,
      );
      setPricingModels(response.data || []);
    } catch (err) {
      // console.error removed
      setPricingModels([]);
    }
  };

  const fetchOrganizationMembers = async () => {
    if (!canEdit) {
      setOrganizationMembers([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_URL}/organizations/${selectedOrg?.id}/members`,
      );
      setOrganizationMembers(response.data || []);
    } catch (err) {
      // console.error removed
      setOrganizationMembers([]);
    }
  };

  const updateBusinessMetricsFromPosition = async (positionData: any) => {
    try {
      // Get TAM data directly from segment endpoint (shouldn't be cascaded)
      const segmentResponse = await axios.get(
        `${API_URL}/segments/${positionData.segment_id}?organization_id=${selectedOrg?.id}`,
      );
      const segment = segmentResponse.data;

      // Extract names and business metrics from position data, TAM from segment
      setBusinessMetrics({
        segment_name: positionData.segment_name || 'Unknown Segment',
        proposition_name:
          positionData.proposition_name || 'Unknown Proposition',
        org_currency: selectedOrg?.currency || 'USD',
        segment_company_count: segment.estimated_company_count || 0, // TAM company count from segment
        segment_tam: segment.total_addressable_market || 0, // TAM value from segment
        estimated_num_of_sam_companies:
          positionData.estimated_num_of_sam_companies || 0,
        estimated_num_of_som_companies:
          positionData.estimated_num_of_som_companies || 0,
        // Real business metrics from analytics endpoint
        total_pipeline_value: positionData.open_pipeline_unweighted || 0,
        total_closed_won_value: positionData.booked_business || 0,
        total_opportunities: positionData.won_opportunities_count || 0, // Won opportunities count
        active_opportunities: positionData.open_opportunities_count || 0, // Open opportunities only
        companies_count: positionData.companies_count || 0, // Unique companies for this intersection
        case_studies_count: positionData.case_studies_count || 0, // Runtime calculated from companies
        testimonials_count: positionData.testimonials_count || 0, // Runtime calculated from companies
        awards_count: positionData.awards_count || 0, // Runtime calculated from companies
      });
    } catch (err) {
      // console.error removed
      // Fallback to just position data without TAM
      setBusinessMetrics({
        segment_name: positionData.segment_name || 'Unknown Segment',
        proposition_name:
          positionData.proposition_name || 'Unknown Proposition',
        org_currency: selectedOrg?.currency || 'USD',
        segment_company_count: 0,
        segment_tam: 0,
        estimated_num_of_sam_companies:
          positionData.estimated_num_of_sam_companies || 0,
        estimated_num_of_som_companies:
          positionData.estimated_num_of_som_companies || 0,
        // Real business metrics from analytics endpoint
        total_pipeline_value: positionData.open_pipeline_unweighted || 0,
        total_closed_won_value: positionData.booked_business || 0,
        total_opportunities: positionData.won_opportunities_count || 0, // Won opportunities count
        active_opportunities: positionData.open_opportunities_count || 0, // Open opportunities only
        companies_count: positionData.companies_count || 0, // Unique companies for this intersection
        case_studies_count: positionData.case_studies_count || 0, // Runtime calculated from companies
        testimonials_count: positionData.testimonials_count || 0, // Runtime calculated from companies
        awards_count: positionData.awards_count || 0, // Runtime calculated from companies
      });
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditedPosition({
      ...editedPosition,
      [field]: value,
    });
  };

  const calculateAnnualValuePerCustomer = () => {
    const selectedPricingModel = pricingModels.find(
      (m) => m.id === editedMarket.pricing_model_id,
    );
    if (!selectedPricingModel || !selectedPricingModel.price) return 0;

    const unitsPerCustomer = Number(editedMarket.units_per_customer) || 1;

    // Calculate annual multiplier based on pricing model frequency
    let annualMultiplier = 1;
    switch (selectedPricingModel.frequency?.toLowerCase()) {
      case 'monthly':
        annualMultiplier = 12;
        break;
      case 'quarterly':
        annualMultiplier = 4;
        break;
      case 'annual':
      case 'yearly':
        annualMultiplier = 1;
        break;
      case 'one_time':
        annualMultiplier = 1;
        break;
      default:
        annualMultiplier = 1;
    }

    return selectedPricingModel.price * annualMultiplier * unitsPerCustomer;
  };

  const calculateSAMValue = () => {
    const annualValuePerCustomer = calculateAnnualValuePerCustomer();
    const samCustomers =
      Number(editedMarket.estimated_num_of_sam_companies) || 0;
    return annualValuePerCustomer * samCustomers;
  };

  const calculateSOMValue = () => {
    const annualValuePerCustomer = calculateAnnualValuePerCustomer();
    const somCustomers =
      Number(editedMarket.estimated_num_of_som_companies) || 0;
    const somTimeframe = Number(editedMarket.som_timeframe_years) || 3;
    return annualValuePerCustomer * somCustomers * somTimeframe;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Only send the changed fields
      const changedFields: any = {};
      // Fields that should not be sent to backend
      const excludeFields = [
        'id',
        'segment_id',
        'proposition_id',
        'owner',
        'created_at',
        'updated_at',
        'obvious_choice_score',
        'relevance_multiplier',
        'proposition_score_snapshot',
        'segment_score_snapshot',
        'obvious_choice_score_updated_at',
        'relevance_multiplier_updated_at',
        'tam_value',
        'pricing_model',
        'sam_evidence',
        'estimated_num_of_sam_companies',
        'estimated_num_of_som_companies',
      ];

      Object.keys(editedPosition).forEach((key) => {
        if (
          !excludeFields.includes(key) &&
          editedPosition[key] !== position?.[key as keyof PositionData]
        ) {
          changedFields[key] = editedPosition[key];
        }
      });

      // Use the new positions API endpoint
      if (!position) {
        throw new Error('Position data not available');
      }
      const positionId = position.id;
      await axios.put(
        `${API_URL}/positions/${positionId}?organization_id=${selectedOrg?.id}`,
        changedFields,
        { headers: getAuthHeaders() },
      );

      await fetchPositionData();
      setEditMode(false);
    } catch (err: any) {
      // console.error removed
      if (err.response) {
        // console.error removed
        // console.error removed
        setError(
          `Failed to save position data: ${err.response.data.detail || 'Unknown error'}`,
        );
      } else {
        setError('Failed to save position data: Network error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleMarketSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Calculate SAM and SOM values based on pricing model and companies
      const selectedPricingModel = pricingModels.find(
        (m) => m.id === editedMarket.pricing_model_id,
      );
      let calculatedSamValue = null;
      let calculatedSomValue = null;

      if (selectedPricingModel && selectedPricingModel.price) {
        const unitsPerCustomer = Number(editedMarket.units_per_customer) || 1;
        let annualMultiplier = 1;

        switch (selectedPricingModel.frequency?.toLowerCase()) {
          case 'monthly':
            annualMultiplier = 12;
            break;
          case 'quarterly':
            annualMultiplier = 4;
            break;
          case 'annual':
          case 'yearly':
            annualMultiplier = 1;
            break;
          case 'one_time':
            annualMultiplier = 1;
            break;
          default:
            annualMultiplier = 1;
        }

        const annualValuePerCustomer =
          selectedPricingModel.price * annualMultiplier * unitsPerCustomer;

        if (editedMarket.estimated_num_of_sam_companies) {
          calculatedSamValue =
            annualValuePerCustomer *
            Number(editedMarket.estimated_num_of_sam_companies);
        }

        if (editedMarket.estimated_num_of_som_companies) {
          const somTimeframe = Number(editedMarket.som_timeframe_years) || 3;
          calculatedSomValue =
            annualValuePerCustomer *
            Number(editedMarket.estimated_num_of_som_companies) *
            somTimeframe;
        }
      }

      // Only send the changed fields
      const changedFields: any = {};
      if (editedMarket.pricing_model_id !== position?.pricing_model_id) {
        // Only send pricing_model_id if it's a valid UUID string, not empty
        if (
          editedMarket.pricing_model_id &&
          editedMarket.pricing_model_id !== ''
        ) {
          changedFields.pricing_model_id = editedMarket.pricing_model_id;
        } else if (editedMarket.pricing_model_id === '') {
          // Send null if empty string to clear the value
          changedFields.pricing_model_id = null;
        }
      }
      if (
        editedMarket.sam_evidence !==
        (position?.sam_evidence || position?.sam_calculation_notes)
      ) {
        changedFields.sam_calculation_notes = editedMarket.sam_evidence;
      }
      if (editedMarket.units_per_customer !== position?.units_per_customer) {
        changedFields.units_per_customer =
          Number(editedMarket.units_per_customer) || 1;
      }
      if (editedMarket.som_timeframe_years !== position?.som_timeframe_years) {
        changedFields.som_timeframe_years =
          Number(editedMarket.som_timeframe_years) || 3;
      }
      if (
        editedMarket.estimated_num_of_sam_companies !==
        position?.estimated_num_of_sam_companies
      ) {
        changedFields.estimated_num_of_sam_companies =
          editedMarket.estimated_num_of_sam_companies
            ? Number(editedMarket.estimated_num_of_sam_companies)
            : null;
      }
      if (
        editedMarket.estimated_num_of_som_companies !==
        position?.estimated_num_of_som_companies
      ) {
        changedFields.estimated_num_of_som_companies =
          editedMarket.estimated_num_of_som_companies
            ? Number(editedMarket.estimated_num_of_som_companies)
            : null;
      }
      // Include calculated values if they've changed
      if (
        calculatedSamValue !== null &&
        calculatedSamValue !== position?.sam_value
      ) {
        changedFields.sam_value = calculatedSamValue;
      }
      if (
        calculatedSomValue !== null &&
        calculatedSomValue !== position?.som_value
      ) {
        changedFields.som_value = calculatedSomValue;
      }

      // No longer need these fields as they've been removed from the backend
      // changedFields.geographic_constraints = [];
      // changedFields.demographic_constraints = [];

      // Update position with market sizing data
      await axios.put(
        `${API_URL}/positions/${position?.id}?organization_id=${selectedOrg?.id}`,
        changedFields,
        {
          headers: getAuthHeaders(),
        },
      );

      await fetchPositionData();
      setMarketEditMode(false);
    } catch (err: any) {
      // console.error removed
      if (err.response) {
        // console.error removed
        // console.error removed
        setError(
          `Failed to save market data: ${err.response.data.detail || 'Unknown error'}`,
        );
      } else {
        setError('Failed to save market data: Network error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleMarketCancel = () => {
    setEditedMarket({
      pricing_model: position?.pricing_model,
      pricing_model_id: position?.pricing_model_id,
      sam_evidence: position?.sam_evidence || position?.sam_calculation_notes,
      sam_value: position?.sam_value,
      som_value: position?.som_value,
      estimated_num_of_sam_companies: position?.estimated_num_of_sam_companies,
      estimated_num_of_som_companies: position?.estimated_num_of_som_companies,
      units_per_customer: position?.units_per_customer || 1,
      som_timeframe_years: position?.som_timeframe_years || 3,
    });
    setMarketEditMode(false);
    setError(null);
  };

  const handleCancel = () => {
    setEditedPosition(position);
    setEditMode(false);
    setError(null);
  };

  const handleFrameworkSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Only send the changed framework fields
      const changedFields: any = {};
      const frameworkFields = [
        'competitive_alternatives',
        'unique_attributes',
        'differentiated_benefits',
        'relevant_trends',
      ];

      frameworkFields.forEach((key) => {
        const editedValue = editedFramework[key];
        // Convert string to array for array fields
        const arrayValue =
          typeof editedValue === 'string'
            ? editedValue
                .split('\n')
                .map((item) => item.trim())
                .filter((item) => item)
            : editedValue || [];

        // Check if the value has changed
        const originalValue = position?.[key as keyof PositionData];
        const hasChanged =
          JSON.stringify(arrayValue) !== JSON.stringify(originalValue || []);

        if (hasChanged) {
          changedFields[key] = arrayValue;
        }
      });

      if (Object.keys(changedFields).length > 0) {
        // Use the new positions API endpoint for Obviously Awesome framework data
        if (!position) {
          throw new Error('Position data not available');
        }
        const positionId = position.id;
        await axios.put(
          `${API_URL}/positions/${positionId}?organization_id=${selectedOrg?.id}`,
          changedFields,
          { headers: getAuthHeaders() },
        );
      }

      await fetchPositionData();
      setFrameworkEditMode(false);
    } catch (err: any) {
      // console.error removed
      if (err.response) {
        // console.error removed
        // console.error removed
        setError(
          `Failed to save framework data: ${err.response.data.detail || 'Unknown error'}`,
        );
      } else {
        setError('Failed to save framework data: Network error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFrameworkCancel = () => {
    // Convert arrays back to newline-separated strings for editing
    setEditedFramework({
      ...position,
      competitive_alternatives: Array.isArray(
        position?.competitive_alternatives,
      )
        ? position?.competitive_alternatives.join('\n')
        : position?.competitive_alternatives || '',
      unique_attributes: Array.isArray(position?.unique_attributes)
        ? position?.unique_attributes.join('\n')
        : position?.unique_attributes || '',
      differentiated_benefits: Array.isArray(position?.differentiated_benefits)
        ? position?.differentiated_benefits.join('\n')
        : position?.differentiated_benefits || '',
      relevant_trends: Array.isArray(position?.relevant_trends)
        ? position?.relevant_trends.join('\n')
        : position?.relevant_trends || '',
    });
    setFrameworkEditMode(false);
    setError(null);
  };

  const handleFrameworkFieldChange = (field: string, value: string) => {
    // For array fields, just store the raw string value
    // We'll convert to array only when saving
    setEditedFramework({ ...editedFramework, [field]: value });
  };

  // SAM Calculator handlers
  const handleSAMCalculatorOpen = async () => {
    if (!position || !selectedOrg?.id) {
      // console.error removed
      return;
    }

    try {
      // Show dialog immediately with connecting message
      setShowSAMCalculator(true);
      setSamChatLoading(true);
      // Focus input after modal renders
      setTimeout(() => samChatInputRef.current?.focus(), 150);
      setSamChatMessages([
        {
          role: 'assistant',
          content: 'Connecting to SAM assistant...',
          timestamp: new Date().toISOString(),
        },
      ]);

      // Start SAM chat conversation
      const response = await axios.post(
        `${API_URL}/sam/segments/${segmentId}/propositions/${propositionId}/sam/start?organization_id=${selectedOrg.id}`,
        {},
      );

      // Log and validate the response
      logSAMChatResponse('Start Conversation', response.data);

      try {
        const validatedResponse = validateSAMChatResponse(response.data);

        // Set the assistant's message
        setSamChatMessages([
          {
            role: 'assistant',
            content: validatedResponse.response,
            timestamp: new Date().toISOString(),
          },
        ]);

        setSuggestedEstimate(validatedResponse.suggested_estimate || null);
      } catch (validationError) {
        // console.error removed
        setSamChatMessages([
          {
            role: 'assistant',
            content:
              "I'm having trouble connecting properly. Please refresh and try again.",
            timestamp: new Date().toISOString(),
          },
        ]);
      }

      setSamChatLoading(false);
      // Focus on input after loading
      setTimeout(() => samChatInputRef.current?.focus(), 100);
    } catch (error: any) {
      // console.error removed
      setSamChatLoading(false);

      let errorMessage =
        'Sorry, I encountered an error connecting. Please try again.';

      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.status === 404) {
          errorMessage =
            'The SAM calculator service is not available. Please contact support.';
        } else if (error.response.status === 500) {
          errorMessage =
            'The server encountered an error. This might be due to data issues. Please try refreshing the page.';
        } else if (error.response.data?.detail) {
          errorMessage = `Error: ${error.response.data.detail}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage =
          'Unable to connect to the server. Please check your connection and try again.';
      }

      setSamChatMessages([
        {
          role: 'assistant',
          content: errorMessage,
          timestamp: new Date().toISOString(),
        },
      ]);
      // Focus on input after loading error
      setTimeout(() => samChatInputRef.current?.focus(), 100);
    }
  };

  const handleSAMChatSubmit = async () => {
    if (!samChatInput.trim() || !position || !selectedOrg?.id) return;

    setSamChatLoading(true);

    try {
      // Add user message to chat
      const userMessage = {
        role: 'user' as const,
        content: samChatInput,
        timestamp: new Date().toISOString(),
      };

      const updatedMessages = [...samChatMessages, userMessage];
      setSamChatMessages(updatedMessages);
      setSamChatInput('');

      // Send to SAM chat API
      const payload = {
        message: samChatInput,
        conversation_history: samChatMessages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
      };

      const response = await axios.post(
        `${API_URL}/sam/segments/${segmentId}/propositions/${propositionId}/sam/chat?organization_id=${selectedOrg.id}`,
        payload,
      );

      // Log and validate the response
      logSAMChatResponse('Send Message', response.data);

      try {
        const validatedResponse = validateSAMChatResponse(response.data);

        // Add assistant response to chat
        const assistantMessage = {
          role: 'assistant' as const,
          content: validatedResponse.response,
          timestamp: new Date().toISOString(),
        };
        setSamChatMessages([...updatedMessages, assistantMessage]);

        setSuggestedEstimate(validatedResponse.suggested_estimate || null);

        // If conversation is complete and we have a final estimate, update the form
        if (validatedResponse.final_estimate) {
          if (marketEditMode) {
            setEditedMarket((prev: any) => ({
              ...prev,
              estimated_num_of_sam_companies:
                validatedResponse.final_estimate?.company_count,
            }));
          } else {
            // Save directly to backend when not in edit mode
            // Calculate SAM value based on pricing model
            let calculatedSamValue = null;
            if (position?.pricing_model_id) {
              const selectedPricingModel = pricingModels.find(
                (m) => m.id === position.pricing_model_id,
              );
              if (selectedPricingModel && selectedPricingModel.price) {
                const unitsPerCustomer = position?.units_per_customer || 1;
                let annualMultiplier = 1;

                switch (selectedPricingModel.frequency?.toLowerCase()) {
                  case 'monthly':
                    annualMultiplier = 12;
                    break;
                  case 'quarterly':
                    annualMultiplier = 4;
                    break;
                  case 'annual':
                  case 'yearly':
                    annualMultiplier = 1;
                    break;
                  case 'one_time':
                    annualMultiplier = 1;
                    break;
                  default:
                    annualMultiplier = 1;
                }

                const annualValuePerCustomer =
                  selectedPricingModel.price *
                  annualMultiplier *
                  unitsPerCustomer;
                calculatedSamValue =
                  annualValuePerCustomer *
                  validatedResponse.final_estimate.company_count;
              }
            }

            const updateData: any = {
              estimated_num_of_sam_companies:
                validatedResponse.final_estimate.company_count,
              sam_calculation_notes: validatedResponse.final_estimate.reasoning,
            };

            if (calculatedSamValue !== null) {
              updateData.sam_value = calculatedSamValue;
            }

            await axios.put(
              `${API_URL}/propositions/${propositionId}/segments/${segmentId}/score?organization_id=${selectedOrg.id}`,
              updateData,
            );
            // Refresh position data to show the update
            await fetchPositionData();
          }

          // Show success notification
          setShowSamSuccess(true);
          setTimeout(() => setShowSamSuccess(false), 3000);

          setShowSAMCalculator(false);
          setSamChatMessages([]);
        }
      } catch (validationError) {
        // console.error removed
        const errorMessage = {
          role: 'assistant' as const,
          content:
            'I apologize, but I had trouble processing the response. Please try again.',
          timestamp: new Date().toISOString(),
        };
        setSamChatMessages([...updatedMessages, errorMessage]);
      }
    } catch (error: any) {
      // console.error removed
      // Add error message to chat
      setSamChatMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'I apologize, but I encountered an error. Please try again.',
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setSamChatLoading(false);
      // Focus back on input after sending message
      setTimeout(() => samChatInputRef.current?.focus(), 100);
    }
  };

  const handleAcceptSamEstimate = async () => {
    if (!suggestedEstimate || !position || !selectedOrg?.id) return;

    // Show loading feedback
    setSamChatLoading(true);
    setSamChatInput('Accepting estimate...');

    try {
      // Send acceptance message
      const payload = {
        message: `I accept your estimate of ${suggestedEstimate.company_count} companies.`,
        conversation_history: samChatMessages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
      };

      const response = await axios.post(
        `${API_URL}/sam/segments/${segmentId}/propositions/${propositionId}/sam/chat?organization_id=${selectedOrg.id}`,
        payload,
      );

      // Log and validate the response
      logSAMChatResponse('Accept Estimate', response.data);

      try {
        const validatedResponse = validateSAMChatResponse(response.data);

        // Only update if we have a final estimate
        if (validatedResponse.final_estimate) {
          // Update form data whether in edit mode or not
          if (marketEditMode) {
            setEditedMarket((prev: any) => ({
              ...prev,
              estimated_num_of_sam_companies:
                validatedResponse.final_estimate?.company_count,
            }));
          } else {
            // Save directly to backend when not in edit mode
            // Calculate SAM value based on pricing model
            let calculatedSamValue = null;
            if (position?.pricing_model_id) {
              const selectedPricingModel = pricingModels.find(
                (m) => m.id === position.pricing_model_id,
              );
              if (selectedPricingModel && selectedPricingModel.price) {
                const unitsPerCustomer = position?.units_per_customer || 1;
                let annualMultiplier = 1;

                switch (selectedPricingModel.frequency?.toLowerCase()) {
                  case 'monthly':
                    annualMultiplier = 12;
                    break;
                  case 'quarterly':
                    annualMultiplier = 4;
                    break;
                  case 'annual':
                  case 'yearly':
                    annualMultiplier = 1;
                    break;
                  case 'one_time':
                    annualMultiplier = 1;
                    break;
                  default:
                    annualMultiplier = 1;
                }

                const annualValuePerCustomer =
                  selectedPricingModel.price *
                  annualMultiplier *
                  unitsPerCustomer;
                calculatedSamValue =
                  annualValuePerCustomer *
                  validatedResponse.final_estimate.company_count;
              }
            }

            const updateData: any = {
              estimated_num_of_sam_companies:
                validatedResponse.final_estimate.company_count,
              sam_calculation_notes: validatedResponse.final_estimate.reasoning,
            };

            if (calculatedSamValue !== null) {
              updateData.sam_value = calculatedSamValue;
            }

            await axios.put(
              `${API_URL}/propositions/${propositionId}/segments/${segmentId}/score?organization_id=${selectedOrg.id}`,
              updateData,
            );
            // Refresh position data to show the update
            await fetchPositionData();
          }

          // Show success notification
          setShowSamSuccess(true);
          setTimeout(() => setShowSamSuccess(false), 3000);

          setShowSAMCalculator(false);
          setSamChatMessages([]);
          setSuggestedEstimate(null);
        } else {
          // If no final estimate, keep the conversation open
          // console.warn removed
        }
      } catch (validationError) {
        // console.error removed
      }
    } catch (error: any) {
      // console.error removed
    } finally {
      setSamChatLoading(false);
      setSamChatInput('');
      // Focus back on input if still showing calculator
      if (showSAMCalculator && samChatInputRef.current) {
        setTimeout(() => samChatInputRef.current?.focus(), 100);
      }
    }
  };

  // Market Relevance scoring handlers
  const handleScoringChange = (component: string, value: number) => {
    setEditedScoring({
      ...editedScoring,
      [component]: value,
    });
  };

  const handleEvidenceChange = (component: string, value: string) => {
    setEditedScoring({
      ...editedScoring,
      [`${component}_evidence`]: value,
    });
  };

  const calculateEditRelevance = () => {
    const components = relevanceComponents.map((comp) => {
      const value = editedScoring[comp.key];
      return typeof value === 'number' ? value : 1.0;
    });
    const sum = components.reduce((acc, val) => acc + val, 0);
    return sum / components.length;
  };

  const calculateEditPositionScore = () => {
    const propScore = position?.proposition_score_snapshot || 0;
    const segScore = position?.segment_score_snapshot || 0;
    const relevance = calculateEditRelevance();
    return ((propScore + segScore) / 2) * relevance;
  };

  const handleScoringSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Only send the changed scoring fields
      const changedFields: any = {};
      relevanceComponents.forEach((comp) => {
        const scoreKey = comp.key;
        const evidenceKey = `${comp.key}_evidence`;

        if (
          editedScoring[scoreKey] !== position?.[scoreKey as keyof PositionData]
        ) {
          changedFields[scoreKey] = editedScoring[scoreKey];
        }
        if (
          editedScoring[evidenceKey] !==
          position?.[evidenceKey as keyof PositionData]
        ) {
          changedFields[evidenceKey] = editedScoring[evidenceKey];
        }
      });

      // Use the new positions API endpoint for all updates including scoring components
      if (!position) {
        throw new Error('Position data not available');
      }
      const positionId = position.id;
      await axios.put(
        `${API_URL}/positions/${positionId}?organization_id=${selectedOrg?.id}`,
        changedFields,
        { headers: getAuthHeaders() },
      );

      await fetchPositionData();
      setScoringEditMode(false);
    } catch (err) {
      // console.error removed
      setError('Failed to save scoring data');
    } finally {
      setSaving(false);
    }
  };

  const handleScoringCancel = () => {
    setEditedScoring(position);
    setScoringEditMode(false);
    setError(null);
  };

  const getMultiplierColor = (value: number | null | undefined) => {
    if (!value) return 'text-gray-500';
    if (value >= 1.2) return 'text-green-600';
    if (value >= 1.0) return 'text-blue-600';
    if (value >= 0.9) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!position) {
    return (
      <div className="p-6">
        <p className="text-lg text-gray-600">Position not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/positioning')}
          className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
        >
          ← Back to Positioning Matrix
        </button>
        <h1 className="text-3xl font-bold">
          {businessMetrics?.proposition_name} × {businessMetrics?.segment_name}
        </h1>
      </div>
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {showSamSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <svg
            className="w-5 h-5 text-green-600 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <p className="text-green-700">
            SAM estimate has been successfully updated!
          </p>
        </div>
      )}
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => handleTabChange('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => handleTabChange('tasks')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tasks'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tasks
          </button>
          <button
            onClick={() => handleTabChange('research')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'research'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Research
          </button>
        </nav>
      </div>
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Business Metrics */}
          <div className="bg-white rounded-lg shadow mb-6 p-6">
            <h2 className="text-xl font-semibold mb-4">Business Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <ClickableMetric
                value={businessMetrics?.total_pipeline_value || 0}
                label="Pipeline Value"
                color="text-gray-800"
                bgColor="bg-gray-50"
                organizationId={selectedOrg?.id || ''}
                metricType="pipeline_value"
                segmentId={segmentId}
                propositionId={propositionId}
                currency={
                  businessMetrics?.org_currency ||
                  selectedOrg?.currency ||
                  'USD'
                }
              />
              <ClickableMetric
                value={businessMetrics?.total_closed_won_value || 0}
                label="Closed Won"
                color="text-gray-800"
                bgColor="bg-gray-50"
                organizationId={selectedOrg?.id || ''}
                metricType="closed_won_value"
                segmentId={segmentId}
                propositionId={propositionId}
                currency={
                  businessMetrics?.org_currency ||
                  selectedOrg?.currency ||
                  'USD'
                }
              />
              <ClickableMetric
                value={businessMetrics?.active_opportunities || 0}
                label="Active Opportunities"
                color="text-gray-800"
                bgColor="bg-gray-50"
                organizationId={selectedOrg?.id || ''}
                metricType="prospects"
                segmentId={segmentId}
                propositionId={propositionId}
              />
              <ClickableMetric
                value={businessMetrics?.total_opportunities || 0}
                label="Won Opportunities"
                color="text-gray-800"
                bgColor="bg-gray-50"
                organizationId={selectedOrg?.id || ''}
                metricType="customers"
                segmentId={segmentId}
                propositionId={propositionId}
              />
            </div>

            {/* Extended metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <ClickableMetric
                value={businessMetrics?.companies_count || 0}
                label="Companies"
                color="text-gray-800"
                bgColor="bg-blue-50"
                organizationId={selectedOrg?.id || ''}
                metricType="companies"
                segmentId={segmentId}
                propositionId={propositionId}
              />
              <ClickableMetric
                value={businessMetrics?.case_studies_count || 0}
                label="Case Studies"
                color="text-gray-800"
                bgColor="bg-green-50"
                organizationId={selectedOrg?.id || ''}
                metricType="case_studies"
                segmentId={segmentId}
                propositionId={propositionId}
              />
              <ClickableMetric
                value={businessMetrics?.testimonials_count || 0}
                label="Testimonials"
                color="text-gray-800"
                bgColor="bg-purple-50"
                organizationId={selectedOrg?.id || ''}
                metricType="testimonials"
                segmentId={segmentId}
                propositionId={propositionId}
              />
              <ClickableMetric
                value={businessMetrics?.awards_count || 0}
                label="Awards"
                color="text-gray-800"
                bgColor="bg-yellow-50"
                organizationId={selectedOrg?.id || ''}
                metricType="awards"
                segmentId={segmentId}
                propositionId={propositionId}
              />
            </div>
          </div>

          {/* Market Sizing & Pricing */}
          <div className="bg-white rounded-lg shadow mb-6 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Market Sizing & Pricing</h2>
              {!marketEditMode && canEdit && (
                <button
                  onClick={() => setMarketEditMode(true)}
                  className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
                >
                  Edit
                </button>
              )}
              {marketEditMode && (
                <div className="flex gap-2">
                  <button
                    onClick={handleMarketCancel}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarketSave}
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {/* Market Values */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* TAM - Read only from segment */}
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">
                  TAM (Total Addressable Market)
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {businessMetrics?.segment_tam
                    ? formatCurrency(
                        businessMetrics.segment_tam,
                        businessMetrics?.org_currency ||
                          selectedOrg?.currency ||
                          'USD',
                        { compact: true },
                      )
                    : 'Not set'}
                </p>
                <p className="text-sm text-gray-500">
                  {businessMetrics?.segment_company_count
                    ? `${businessMetrics.segment_company_count.toLocaleString()} companies`
                    : ''}
                </p>
              </div>

              {/* SAM - Shows calculated value based on companies and pricing */}
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">
                  SAM (Serviceable Addressable Market)
                </p>
                {marketEditMode ? (
                  <div>
                    <p className="text-2xl font-bold text-blue-600 mb-2">
                      {formatCurrency(
                        calculateSAMValue(),
                        businessMetrics?.org_currency ||
                          selectedOrg?.currency ||
                          'USD',
                        { compact: true },
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      Calculated automatically
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-blue-600">
                      {position?.sam_value
                        ? formatCurrency(
                            position.sam_value,
                            businessMetrics?.org_currency ||
                              selectedOrg?.currency ||
                              'USD',
                            { compact: true },
                          )
                        : 'Not calculated'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {position?.estimated_num_of_sam_companies
                        ? `${position.estimated_num_of_sam_companies.toLocaleString()} companies`
                        : ''}
                    </p>
                  </>
                )}
              </div>

              {/* SOM - Shows calculated value based on companies and pricing */}
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">
                  SOM (Serviceable Obtainable Market)
                </p>
                {marketEditMode ? (
                  <div>
                    <p className="text-2xl font-bold text-green-600 mb-2">
                      {formatCurrency(
                        calculateSOMValue(),
                        businessMetrics?.org_currency ||
                          selectedOrg?.currency ||
                          'USD',
                        { compact: true },
                      )}
                    </p>
                    <p className="text-xs text-gray-500 mb-2">
                      Over {editedMarket.som_timeframe_years || 3} years
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-green-600">
                      {position?.som_value
                        ? formatCurrency(
                            position.som_value,
                            businessMetrics?.org_currency ||
                              selectedOrg?.currency ||
                              'USD',
                            { compact: true },
                          )
                        : 'Not calculated'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {position?.estimated_num_of_som_companies
                        ? `${position.estimated_num_of_som_companies.toLocaleString()} companies`
                        : ''}
                      {position?.som_timeframe_years &&
                        ` over ${position.som_timeframe_years} years`}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Pricing and Calculation Fields */}
            {marketEditMode ? (
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pricing Model
                  </label>
                  {pricingModels.length > 0 ? (
                    <select
                      value={editedMarket.pricing_model_id || ''}
                      onChange={(e) => {
                        const selectedModel = pricingModels.find(
                          (m) => m.id === e.target.value,
                        );
                        setEditedMarket({
                          ...editedMarket,
                          pricing_model_id: e.target.value,
                          pricing_model: selectedModel?.name || '',
                        });
                      }}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="">Select a pricing model...</option>
                      {pricingModels.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.name} - {formatPricingType(model.pricing_type)}{' '}
                          (
                          {formatCurrency(
                            model.price || 0,
                            businessMetrics?.org_currency || 'USD',
                          )}
                          )
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      No pricing models defined for this proposition.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Units per Customer
                  </label>
                  <input
                    type="number"
                    value={editedMarket.units_per_customer || ''}
                    onChange={(e) =>
                      setEditedMarket({
                        ...editedMarket,
                        units_per_customer: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-md"
                    placeholder="Number of pricing units (users, teams, etc.) per customer"
                  />
                </div>

                {/* Annual Value per Customer Display */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700">
                    Annual Value per Customer
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(
                      calculateAnnualValuePerCustomer(),
                      businessMetrics?.org_currency || 'USD',
                      {
                        compact: false,
                      },
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(() => {
                      const model = pricingModels.find(
                        (m) => m.id === editedMarket.pricing_model_id,
                      );
                      if (!model)
                        return 'Select a pricing model to see calculation';
                      const units =
                        Number(editedMarket.units_per_customer) || 1;
                      return `${formatCurrency(Number(model.price || 0), businessMetrics?.org_currency || 'USD', { compact: false })} × ${formatPricingType(model.frequency || 'annual')} × ${units} units`;
                    })()}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Number of SAM Companies
                  </label>
                  <input
                    type="number"
                    value={editedMarket.estimated_num_of_sam_companies || ''}
                    onChange={(e) =>
                      setEditedMarket({
                        ...editedMarket,
                        estimated_num_of_sam_companies: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-md"
                    placeholder="Number of companies you can realistically serve"
                  />
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">
                      SAM Value (Annual)
                    </p>
                    <p className="text-lg font-bold text-blue-600">
                      {formatCurrency(
                        calculateSAMValue(),
                        businessMetrics?.org_currency || 'USD',
                        { compact: true },
                      )}
                    </p>
                    <p className="text-xs text-blue-700">
                      {Number(editedMarket.estimated_num_of_sam_companies) || 0}{' '}
                      customers ×{' '}
                      {formatCurrency(
                        calculateAnnualValuePerCustomer(),
                        businessMetrics?.org_currency || 'USD',
                        {
                          compact: false,
                        },
                      )}{' '}
                      annual value
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Number of SOM Companies
                  </label>
                  <input
                    type="number"
                    value={editedMarket.estimated_num_of_som_companies || ''}
                    onChange={(e) =>
                      setEditedMarket({
                        ...editedMarket,
                        estimated_num_of_som_companies: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-md"
                    placeholder="Number of companies you can realistically win"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SOM Timeframe (Years)
                  </label>
                  <input
                    type="number"
                    value={editedMarket.som_timeframe_years || 3}
                    onChange={(e) =>
                      setEditedMarket({
                        ...editedMarket,
                        som_timeframe_years: e.target.value,
                      })
                    }
                    className="w-full p-2 border rounded-md"
                    placeholder="Timeline for achieving SOM (default: 3 years)"
                  />
                  <div className="mt-2 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-green-900">
                      SOM Value (Total)
                    </p>
                    <p className="text-lg font-bold text-green-600">
                      {formatCurrency(
                        calculateSOMValue(),
                        businessMetrics?.org_currency || 'USD',
                        { compact: true },
                      )}
                    </p>
                    <p className="text-xs text-green-700">
                      {Number(editedMarket.estimated_num_of_som_companies) || 0}{' '}
                      customers ×{' '}
                      {formatCurrency(
                        calculateAnnualValuePerCustomer(),
                        businessMetrics?.org_currency || 'USD',
                        {
                          compact: false,
                        },
                      )}{' '}
                      annual value ×{' '}
                      {Number(editedMarket.som_timeframe_years) || 3} years
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pricing Model
                </label>
                <p className="text-gray-900">
                  {(() => {
                    const model = pricingModels.find(
                      (m) => m.id === position?.pricing_model_id,
                    );
                    return model
                      ? `${model.name} - ${formatPricingType(model.pricing_type)}`
                      : position?.pricing_model || 'Not specified';
                  })()}
                </p>
                {position?.units_per_customer && (
                  <p className="text-sm text-gray-600 mt-1">
                    Units per customer: {position.units_per_customer}
                  </p>
                )}
              </div>
            )}

            {/* SAM Explanation */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SAM Explanation
              </label>
              {marketEditMode ? (
                <textarea
                  rows={3}
                  value={editedMarket.sam_evidence || ''}
                  onChange={(e) =>
                    setEditedMarket({
                      ...editedMarket,
                      sam_evidence: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded-md"
                  placeholder="Explanation for SAM calculation..."
                />
              ) : (
                <p className="text-gray-900 whitespace-pre-wrap">
                  {position?.sam_evidence ||
                    position?.sam_calculation_notes ||
                    'Not specified'}
                </p>
              )}
            </div>

            <div className="text-center">
              {canEdit && hasGenAIAccess ? (
                <button
                  onClick={handleSAMCalculatorOpen}
                  className="inline-flex items-center gap-2 px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  SAM Estimation Calculator
                </button>
              ) : canEdit && !hasGenAIAccess ? (
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <svg
                    className="w-5 h-5 text-blue-600 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <div className="text-left">
                    <p className="text-sm text-blue-800 font-medium">
                      SAM Calculator requires{' '}
                      {planType === 'free' ? 'a paid plan' : 'plan upgrade'}
                    </p>
                    <p className="text-xs text-blue-700">
                      {isAdmin ? (
                        <a
                          href="/organization?tab=billing"
                          className="underline hover:text-blue-900"
                        >
                          Upgrade your plan
                        </a>
                      ) : (
                        'Contact your organization admin'
                      )}
                    </p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Two Column Layout for Strategy and Scoring */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Left Column - Core Strategy & Obviously Awesome (2/3 width) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Core Strategy Fields */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Core Strategy</h2>
                  {!editMode && canEdit && (
                    <button
                      onClick={() => setEditMode(true)}
                      className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
                    >
                      Edit
                    </button>
                  )}
                  {editMode && (
                    <div className="flex gap-2">
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Focus Mode
                    </label>
                    {editMode ? (
                      <select
                        value={editedPosition.focus_mode || ''}
                        onChange={(e) =>
                          handleFieldChange('focus_mode', e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="">Select focus mode</option>
                        {focusModeOptions.map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${getFocusModeTailwindClasses(position.focus_mode, true)}`}
                      >
                        {formatFocusMode(position.focus_mode) || 'Not set'}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Owner
                    </label>
                    {editMode ? (
                      <select
                        value={editedPosition.owner_id || ''}
                        onChange={(e) =>
                          handleFieldChange('owner_id', e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                      >
                        <option value="">None</option>
                        {organizationMembers.map((member: any) => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.user_name || member.user_email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-gray-900">
                        {position.owner ||
                          (position.owner_id &&
                            organizationMembers.find(
                              (m) => m.user_id === position.owner_id,
                            )?.user_name) ||
                          (position.owner_id &&
                            organizationMembers.find(
                              (m) => m.user_id === position.owner_id,
                            )?.user_email) ||
                          'Not assigned'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Why is this the Obvious Choice?
                    </label>
                    {editMode ? (
                      <textarea
                        rows={3}
                        value={editedPosition.why_obvious_choice || ''}
                        onChange={(e) =>
                          handleFieldChange(
                            'why_obvious_choice',
                            e.target.value,
                          )
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="Explain why this proposition is the obvious choice for this segment..."
                      />
                    ) : (
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {position.why_obvious_choice || 'Not specified'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gaps to Address
                    </label>
                    {editMode ? (
                      <textarea
                        rows={3}
                        value={editedPosition.gaps_to_address || ''}
                        onChange={(e) =>
                          handleFieldChange('gaps_to_address', e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="What gaps need to be addressed to improve positioning..."
                      />
                    ) : (
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {position.gaps_to_address || 'Not specified'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Actions to Improve
                    </label>
                    {editMode ? (
                      <textarea
                        rows={3}
                        value={editedPosition.actions_to_improve || ''}
                        onChange={(e) =>
                          handleFieldChange(
                            'actions_to_improve',
                            e.target.value,
                          )
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="Specific actions to take to improve positioning..."
                      />
                    ) : (
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {position.actions_to_improve || 'Not specified'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rationale
                    </label>
                    {editMode ? (
                      <textarea
                        rows={3}
                        value={editedPosition.rationale || ''}
                        onChange={(e) =>
                          handleFieldChange('rationale', e.target.value)
                        }
                        className="w-full p-2 border rounded-md"
                        placeholder="Strategic rationale for this positioning..."
                      />
                    ) : (
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {position.rationale || 'Not specified'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Obviously Awesome Framework */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    Obviously Awesome Framework
                  </h2>
                  {!frameworkEditMode && canEdit && (
                    <button
                      onClick={() => {
                        // Initialize framework data with string values for editing
                        setEditedFramework({
                          ...position,
                          competitive_alternatives: Array.isArray(
                            position?.competitive_alternatives,
                          )
                            ? position?.competitive_alternatives.join('\n')
                            : position?.competitive_alternatives || '',
                          unique_attributes: Array.isArray(
                            position?.unique_attributes,
                          )
                            ? position?.unique_attributes.join('\n')
                            : position?.unique_attributes || '',
                          differentiated_benefits: Array.isArray(
                            position?.differentiated_benefits,
                          )
                            ? position?.differentiated_benefits.join('\n')
                            : position?.differentiated_benefits || '',
                          relevant_trends: Array.isArray(
                            position?.relevant_trends,
                          )
                            ? position?.relevant_trends.join('\n')
                            : position?.relevant_trends || '',
                        });
                        setFrameworkEditMode(true);
                      }}
                      className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
                    >
                      Edit
                    </button>
                  )}
                  {frameworkEditMode && (
                    <div className="flex gap-2">
                      <button
                        onClick={handleFrameworkCancel}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleFrameworkSave}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Competitive Alternatives
                    </label>
                    {frameworkEditMode ? (
                      <textarea
                        rows={4}
                        value={editedFramework.competitive_alternatives || ''}
                        onChange={(e) => {
                          handleFrameworkFieldChange(
                            'competitive_alternatives',
                            e.target.value,
                          );
                        }}
                        onKeyDown={(_e) => {
                          // Allow all keys - don't prevent any default behavior
                        }}
                        className="w-full p-2 border rounded-md resize-none"
                        placeholder="What would customers do if this solution didn't exist? (one per line)"
                        style={{ whiteSpace: 'pre-wrap' }}
                      />
                    ) : (
                      <div className="text-gray-900">
                        {position.competitive_alternatives?.length
                          ? position.competitive_alternatives.map(
                              (item, index) => <div key={index}>• {item}</div>,
                            )
                          : 'Not specified'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unique Attributes
                    </label>
                    {frameworkEditMode ? (
                      <textarea
                        rows={4}
                        value={editedFramework.unique_attributes || ''}
                        onChange={(e) =>
                          handleFrameworkFieldChange(
                            'unique_attributes',
                            e.target.value,
                          )
                        }
                        className="w-full p-2 border rounded-md resize-none"
                        placeholder="What features or capabilities do you have that alternatives don't? (one per line)"
                        style={{ whiteSpace: 'pre-wrap' }}
                      />
                    ) : (
                      <div className="text-gray-900">
                        {position.unique_attributes?.map((item, index) => (
                          <div key={index}>• {item}</div>
                        )) || 'Not specified'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Differentiated Benefits
                    </label>
                    {frameworkEditMode ? (
                      <textarea
                        rows={4}
                        value={editedFramework.differentiated_benefits || ''}
                        onChange={(e) =>
                          handleFrameworkFieldChange(
                            'differentiated_benefits',
                            e.target.value,
                          )
                        }
                        className="w-full p-2 border rounded-md resize-none"
                        placeholder="Key value themes and outcomes that differentiate you (one per line)"
                        style={{ whiteSpace: 'pre-wrap' }}
                      />
                    ) : (
                      <div className="text-gray-900">
                        {position.differentiated_benefits?.map(
                          (item, index) => <div key={index}>• {item}</div>,
                        ) || 'Not specified'}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Relevant Trends
                    </label>
                    {frameworkEditMode ? (
                      <textarea
                        rows={4}
                        value={editedFramework.relevant_trends || ''}
                        onChange={(e) =>
                          handleFrameworkFieldChange(
                            'relevant_trends',
                            e.target.value,
                          )
                        }
                        className="w-full p-2 border rounded-md resize-none"
                        placeholder="Trends that make your product more relevant (one per line)"
                        style={{ whiteSpace: 'pre-wrap' }}
                      />
                    ) : (
                      <div className="text-gray-900">
                        {position.relevant_trends?.map((item, index) => (
                          <div key={index}>• {item}</div>
                        )) || 'Not specified'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Position Strength Score (1/3 width) */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">
                    Position Strength Score
                  </h2>
                  {!scoringEditMode && canEdit && (
                    <button
                      onClick={() => {
                        setEditedScoring({ ...position });
                        setScoringEditMode(true);
                      }}
                      className="px-3 py-1 text-sm text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50"
                    >
                      Edit
                    </button>
                  )}
                  {scoringEditMode && (
                    <div className="flex gap-2">
                      <button
                        onClick={handleScoringCancel}
                        className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleScoringSave}
                        disabled={saving}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Score Calculation Display */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <div className="text-center">
                    <p className="text-5xl font-bold text-blue-600 mb-2">
                      {scoringEditMode
                        ? calculateEditPositionScore().toFixed(1)
                        : calculatePositioningScore(
                            position,
                            selectedOrg?.id,
                          ).toFixed(1)}
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-gray-600">Prop</p>
                        <p className="font-semibold">
                          {(position.proposition_score_snapshot ?? 0).toFixed(
                            1,
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Seg</p>
                        <p className="font-semibold">
                          {(position.segment_score_snapshot ?? 0).toFixed(1)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600">Rel</p>
                        <p
                          className={`font-semibold ${getMultiplierColor(scoringEditMode ? calculateEditRelevance() : position.relevance_multiplier)}`}
                        >
                          {scoringEditMode
                            ? calculateEditRelevance().toFixed(2)
                            : (position.relevance_multiplier ?? 1.0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  {!scoringEditMode &&
                    position.obvious_choice_score_updated_at && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        Updated:{' '}
                        {format(
                          new Date(position.obvious_choice_score_updated_at),
                          'MMM d, yyyy',
                        )}
                      </p>
                    )}
                </div>

                {/* Market Relevance Components */}
                <div>
                  <h3 className="text-sm font-semibold mb-3">
                    Market Relevance Components
                  </h3>
                  <div className="space-y-3">
                    {relevanceComponents.map((component) => {
                      const value = scoringEditMode
                        ? editedScoring[component.key]
                        : position[component.key as keyof PositionData];
                      const evidence = scoringEditMode
                        ? editedScoring[`${component.key}_evidence`]
                        : position[
                            `${component.key}_evidence` as keyof PositionData
                          ];

                      return (
                        <div
                          key={component.key}
                          className="border rounded-lg p-3"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div>
                              <h4 className="text-sm font-medium">
                                {component.label}
                              </h4>
                              <p className="text-xs text-gray-600">
                                {component.description}
                              </p>
                            </div>
                            {!scoringEditMode && (
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full border ${
                                  value && value >= 1.2
                                    ? 'bg-green-100 text-green-800 border-green-300'
                                    : value && value >= 1.0
                                      ? 'bg-blue-100 text-blue-800 border-blue-300'
                                      : value && value >= 0.9
                                        ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                                        : 'bg-red-100 text-red-800 border-red-300'
                                }`}
                              >
                                {(value ?? 1.0).toFixed(2)}
                              </span>
                            )}
                          </div>
                          {scoringEditMode ? (
                            <>
                              <div className="mb-2">
                                <input
                                  type="range"
                                  min={component.min}
                                  max={component.max}
                                  step={component.step}
                                  value={value || 1.0}
                                  onChange={(e) =>
                                    handleScoringChange(
                                      component.key,
                                      parseFloat(e.target.value),
                                    )
                                  }
                                  className="w-full"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                  <span>0.8</span>
                                  <span className="font-medium">
                                    {(value || 1.0).toFixed(2)}
                                  </span>
                                  <span>1.3</span>
                                </div>
                              </div>
                              <textarea
                                rows={2}
                                placeholder="Add evidence..."
                                value={evidence || ''}
                                onChange={(e) =>
                                  handleEvidenceChange(
                                    component.key,
                                    e.target.value,
                                  )
                                }
                                className="w-full p-2 text-sm border rounded-md"
                              />
                            </>
                          ) : (
                            evidence && (
                              <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                <p>{evidence}</p>
                              </div>
                            )
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Relevance Guide */}
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <h4 className="text-sm font-semibold mb-1">
                      Relevance Guide
                    </h4>
                    <div className="space-y-1 text-xs">
                      <p>
                        <strong>0.8-0.9:</strong> Below average (dampener)
                      </p>
                      <p>
                        <strong>0.9-1.1:</strong> Average (neutral)
                      </p>
                      <p>
                        <strong>1.1-1.3:</strong> Above average (multiplier)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SAM Calculator Modal */}
          {showSAMCalculator && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="p-4 sm:p-6 border-b">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                      <svg
                        className="w-6 h-6 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      SAM Estimation Assistant
                    </h2>
                    <button
                      onClick={() => {
                        setShowSAMCalculator(false);
                        setSamChatInput('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 hidden sm:block">
                    Let me help you estimate how many companies are in your
                    serviceable addressable market
                  </p>
                </div>

                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                  <div className="space-y-4">
                    {samChatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-4 ${
                            msg.role === 'user'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{msg.content}</p>
                          {msg.timestamp && (
                            <p
                              className={`text-xs mt-2 ${msg.role === 'user' ? 'text-blue-100' : 'text-gray-500'}`}
                            >
                              {format(new Date(msg.timestamp), 'HH:mm')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Suggested Estimate Card */}
                    {suggestedEstimate && (
                      <div className="mb-2">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                          <div className="flex flex-col">
                            <div>
                              <h4 className="text-sm font-semibold text-blue-900 mb-1">
                                💡 Suggested Estimate
                              </h4>
                              <p className="text-base sm:text-lg font-bold text-blue-900">
                                {suggestedEstimate.company_count.toLocaleString()}{' '}
                                companies
                              </p>
                              <p className="text-xs sm:text-sm text-blue-700 mt-2">
                                {suggestedEstimate.reasoning}
                              </p>
                              <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                <button
                                  onClick={handleAcceptSamEstimate}
                                  disabled={samChatLoading}
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                  {samChatLoading ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                      <span className="hidden sm:inline">
                                        Applying...
                                      </span>
                                    </>
                                  ) : (
                                    <span>
                                      <span className="inline sm:hidden">
                                        Accept
                                      </span>
                                      <span className="hidden sm:inline">
                                        Accept This Estimate
                                      </span>
                                    </span>
                                  )}
                                </button>
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    suggestedEstimate.confidence === 'high'
                                      ? 'bg-green-100 text-green-800'
                                      : suggestedEstimate.confidence === 'low'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  Confidence:{' '}
                                  {suggestedEstimate.confidence || 'medium'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {samChatLoading && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            <span className="text-gray-600">
                              Assistant is thinking...
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Chat Input */}
                <div className="p-4 sm:p-6 border-t">
                  <div className="flex gap-2">
                    <input
                      ref={samChatInputRef}
                      type="text"
                      value={samChatInput}
                      onChange={(e) => setSamChatInput(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        handleSAMChatSubmit()
                      }
                      placeholder="Ask about market size, competition, pricing..."
                      className="flex-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={samChatLoading}
                    />
                    <button
                      onClick={handleSAMChatSubmit}
                      disabled={!samChatInput.trim() || samChatLoading}
                      className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
                    >
                      {samChatLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="hidden sm:inline">
                            Processing...
                          </span>
                        </>
                      ) : (
                        'Send'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}{' '}
      {/* Close overview tab */}
      {/* Tasks Tab */}
      {activeTab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Tasks</h2>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setTaskViewMode(taskViewMode === 'kanban' ? 'list' : 'kanban')
                }
                className="p-2 border border-gray-300 rounded hover:bg-gray-50"
                title={
                  taskViewMode === 'kanban'
                    ? 'Switch to List View'
                    : 'Switch to Kanban View'
                }
              >
                {taskViewMode === 'kanban' ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 10h16M4 14h16M4 18h16"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {taskViewMode === 'kanban' ? (
            <TaskKanbanView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="position"
              entityId={position?.id || `${propositionId}_${segmentId}`}
              canEdit={canEdit}
              segments={[]}
              propositions={[]}
              positions={
                position
                  ? [
                      {
                        id: position.id,
                        proposition_name: position.proposition_name,
                        segment_name: position.segment_name,
                      },
                    ]
                  : []
              }
              groups={[]}
              companies={[]}
            />
          ) : (
            <TaskListView
              tasks={tasks}
              onTaskUpdate={handleTaskUpdate}
              onTaskCreate={handleTaskCreate}
              onTaskDelete={handleTaskDelete}
              organizationId={selectedOrg?.id || ''}
              entityType="position"
              entityId={position?.id || `${propositionId}_${segmentId}`}
              canEdit={canEdit}
              segments={[]}
              propositions={[]}
              positions={
                position
                  ? [
                      {
                        id: position.id,
                        proposition_name: position.proposition_name,
                        segment_name: position.segment_name,
                      },
                    ]
                  : []
              }
              groups={[]}
              companies={[]}
            />
          )}
        </div>
      )}
      {/* Research Tab */}
      {activeTab === 'research' && position && selectedOrg && (
        <ResearchTab
          organizationId={selectedOrg.id}
          entityType={ResearchEntityType.POSITION}
          entityId={position.id}
          entityName={`${position.proposition_name} × ${position.segment_name}`}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
